import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  inputBaseClasses,
  TextField,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useModals } from "../../store/dialogs/hooks";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Transition } from "../../pages/app/utils/animation";

const AnnotationSchema = z.object({
  annotationName: z
    .string()
    .trim()
    .nonempty({ message: "A name for the annotation is required." }),
});

export const AnnotationDialog = ({ onCreate }) => {
  const { closeAnnotationDialog, showAnnotationDialog } = useModals();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(AnnotationSchema),
    defaultValues: {
      annotationName: "",
    },
    mode: "all",
  });

  const handleCancel = () => {
    reset();
    closeAnnotationDialog();
  };

  const handleClear = () => {
    reset();
    trigger("annotationName");
  };

  const handleClose = () => {
    !isValid && reset();
    closeAnnotationDialog();
  };

  const onSubmit = ({ annotationName }) => {
    onCreate(annotationName);
    closeAnnotationDialog();
  };

  return (
    <Dialog
      aria-hidden={!showAnnotationDialog}
      closeAfterTransition={false}
      maxWidth="xs"
      fullWidth
      aria-labelledby="annotation-dialog-title"
      aria-describedby="annotation-dialog-description"
      onClose={handleClose}
      open={showAnnotationDialog}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle id="annotation-dialog-title">
          Create Annotation
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <DialogContentText id="annotation-dialog-description">
            Please type in the name of the annotation below. If the name matches
            an existing annotation, the new regions will be added to the
            existing regions.
          </DialogContentText>
          <Controller
            name="annotationName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Annotation Name"
                margin="normal"
                error={!!errors.annotationName}
                helperText={
                  errors.annotationName
                    ? errors.annotationName?.message
                    : "Please enter a name for the annotation."
                }
                required
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{
                          [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                            {
                              opacity: 0,
                              pointerEvents: "none",
                            },
                        }}
                      >
                        <IconButton
                          aria-label="clear input"
                          onClick={handleClear}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button disabled={!isValid} type="submit" variant="contained">
            Create
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
